import React, { useMemo, useState } from 'react';

import { useIsOverflowing } from '@tanium/react-use-is-overflowing';

import { Legend } from '../components/Legend';
import { ChartItemClickEventHandler } from '../utils/ChartProps';
import { ColorScale } from '../utils/ColorScale';
import { DataKeyGetter, DataKeyType, LabelGetter, ValueGetter } from '../utils/DataProps';

interface UseDotLegendItemsProps<TDataKeyType extends DataKeyType, TPointType, TDatum> {
  colorScale: ColorScale<TDataKeyType>;
  data: readonly Readonly<TDatum>[];
  dataKeyGetter: DataKeyGetter<TDatum, TDataKeyType>;
  labelGetter: LabelGetter<TDatum>;
  onClick: ChartItemClickEventHandler<TPointType[], TDataKeyType, TDatum> | undefined;
  valueGetter: ValueGetter<TDatum, TPointType[]>;
}

interface UseDotLegendItemsReturnType<TDataKeyType> {
  activeDataKey: TDataKeyType | undefined;
  legend: React.ReactNode;
}

export const DEFAULT_LEGEND_HEIGHT_PX = 32;

export const useDotLegendItems = <TDataKeyType extends DataKeyType, TPointType, TDatum>({
  colorScale,
  data,
  dataKeyGetter,
  labelGetter,
  onClick,
  valueGetter,
}: UseDotLegendItemsProps<TDataKeyType, TPointType, TDatum>): UseDotLegendItemsReturnType<TDataKeyType> => {
  const [isOverflowing, ref] = useIsOverflowing({ updateOnResize: true, dimension: 'height' });

  const [activeDataKey, setActiveDataKey] = useState<TDataKeyType>();

  const legend = useMemo(() => {
    return (
      <Legend
        ref={ref}
        maxHeight={`${DEFAULT_LEGEND_HEIGHT_PX}px`}
        overflowY={isOverflowing ? 'auto' : 'visible'}
        lineHeight="16px"
        items={data.map((d, i) => {
          const dataKey = dataKeyGetter(d);
          const label = labelGetter(d);
          const value = valueGetter(d);
          const color = colorScale.get(dataKey);

          const handleClick = onClick
            ? (e: React.MouseEvent) => {
                onClick({ item: d, domEvent: e, dataKey, label, value, color, index: i, data });
              }
            : undefined;

          return {
            color,
            label,
            onClick: handleClick,
            key: dataKey,
            onPointerOut: () => {
              setActiveDataKey(undefined);
            },
            onPointerOver: () => {
              setActiveDataKey(dataKey);
            },
          };
        })}
      />
    );
  }, [colorScale, data, dataKeyGetter, isOverflowing, labelGetter, onClick, ref, valueGetter]);

  return {
    activeDataKey,
    legend,
  };
};
